/**
 * Beta Tester Management
 * 
 * Handles beta tester status, access control, and transition to trial
 */

import { doc, getDoc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';

// ============================================================================
// CONFIGURATION
// ============================================================================

/**
 * Fallback beta end date — only used if Firestore config is unavailable.
 * The authoritative value lives in beta_config/settings.betaEndDate.
 */
const BETA_END_DATE_FALLBACK = new Date('2026-05-01');

/** Cached beta end date from Firestore (avoids re-fetching every call). */
let _cachedBetaEndDate: Date | null = null;
let _cacheTimestamp = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch the beta end date from Firestore config, with local cache.
 */
async function fetchBetaEndDate(): Promise<Date> {
  const now = Date.now();
  if (_cachedBetaEndDate && now - _cacheTimestamp < CACHE_TTL_MS) {
    return _cachedBetaEndDate;
  }
  try {
    const configSnap = await getDoc(doc(db, 'beta_config', 'settings'));
    if (configSnap.exists()) {
      const data = configSnap.data();
      const endDate = data.betaEndDate?.toDate?.() ?? (data.betaEndDate ? new Date(data.betaEndDate) : null);
      if (endDate && !isNaN(endDate.getTime())) {
        _cachedBetaEndDate = endDate;
        _cacheTimestamp = now;
        return endDate;
      }
    }
  } catch (error) {
    console.error('Error fetching beta end date from config:', error);
  }
  return BETA_END_DATE_FALLBACK;
}

/**
 * Exported getter so other modules can display the end date.
 */
export async function getBetaEndDate(): Promise<Date> {
  return fetchBetaEndDate();
}

/**
 * Synchronous fallback — used where async is not possible.
 * Returns cached value if available, otherwise the fallback constant.
 */
export function getBetaEndDateSync(): Date {
  return _cachedBetaEndDate ?? BETA_END_DATE_FALLBACK;
}

/**
 * Check if the beta period is currently active.
 * Synchronous — uses cached/fallback date. Call getBetaEndDate() first
 * to warm the cache if you need the Firestore value.
 */
export function isBetaPeriodActive(): boolean {
  const endDate = getBetaEndDateSync();
  return new Date() < endDate;
}

/**
 * Async version that always checks Firestore config.
 */
export async function isBetaPeriodActiveAsync(): Promise<boolean> {
  const endDate = await fetchBetaEndDate();
  return new Date() < endDate;
}

// ============================================================================
// BETA TESTER STATUS
// ============================================================================

export interface BetaTesterInfo {
  isBetaTester: boolean;
  betaStartDate?: Date;
  betaEndDate?: Date;
  hasAccess: boolean; // Combined: is beta tester AND beta period is active
}

/**
 * Check if a user is a beta tester with active access
 */
export async function checkBetaTesterStatus(userId: string): Promise<BetaTesterInfo> {
  try {
    console.log('� Checking beta tester status for user:', userId);
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      console.log(' User document not found');
      return {
        isBetaTester: false,
        hasAccess: false,
      };
    }

    const userData = userDoc.data();
    const isBeta = userData?.isBetaTester === true;
    const betaActive = isBetaPeriodActive();
    
    console.log('� Beta check results:', {
      isBetaTester: isBeta,
      betaPeriodActive: betaActive,
      hasAccess: isBeta && betaActive,
      betaEndDate: getBetaEndDateSync().toISOString(),
    });
    
    return {
      isBetaTester: isBeta,
      betaStartDate: userData?.betaStartDate?.toDate(),
      betaEndDate: userData?.betaEndDate?.toDate(),
      hasAccess: isBeta && betaActive,
    };
  } catch (error) {
    console.error(' Error checking beta tester status:', error);
    return {
      isBetaTester: false,
      hasAccess: false,
    };
  }
}

// ============================================================================
// BETA TESTER MANAGEMENT (Admin Functions)
// ============================================================================

/**
 * Add a user as a beta tester
 */
export async function addBetaTester(userId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'users', userId), {
      isBetaTester: true,
      betaStartDate: new Date(),
    });
    console.log(` Added beta tester: ${userId}`);
  } catch (error) {
    console.error(` Error adding beta tester ${userId}:`, error);
    throw error;
  }
}

/**
 * Remove beta tester status from a user
 */
export async function removeBetaTester(userId: string): Promise<void> {
  try {
    await updateDoc(doc(db, 'users', userId), {
      isBetaTester: false,
      betaEndDate: new Date(),
    });
    console.log(` Removed beta tester: ${userId}`);
  } catch (error) {
    console.error(` Error removing beta tester ${userId}:`, error);
    throw error;
  }
}

/**
 * Get all beta testers
 */
export async function getAllBetaTesters(): Promise<Array<{
  userId: string;
  email: string;
  betaStartDate?: Date;
  betaEndDate?: Date;
}>> {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('isBetaTester', '==', true));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      userId: doc.id,
      email: doc.data().email || 'No email',
      betaStartDate: doc.data().betaStartDate?.toDate(),
      betaEndDate: doc.data().betaEndDate?.toDate(),
    }));
  } catch (error) {
    console.error(' Error fetching beta testers:', error);
    return [];
  }
}

/**
 * Remove all beta testers (use when ending beta period)
 */
export async function removeAllBetaTesters(): Promise<{
  success: number;
  failed: number;
  errors: Array<{ userId: string; error: string }>;
}> {
  const results = {
    success: 0,
    failed: 0,
    errors: [] as Array<{ userId: string; error: string }>,
  };

  try {
    const betaTesters = await getAllBetaTesters();
    console.log(` Removing beta access from ${betaTesters.length} users...`);

    for (const tester of betaTesters) {
      try {
        await removeBetaTester(tester.userId);
        results.success++;
      } catch (error) {
        results.failed++;
        results.errors.push({
          userId: tester.userId,
          error: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    }

    console.log(` Beta removal complete: ${results.success} success, ${results.failed} failed`);
    return results;
  } catch (error) {
    console.error(' Error removing all beta testers:', error);
    throw error;
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get days remaining in beta period (async — reads Firestore config).
 */
export async function getDaysRemainingInBeta(): Promise<number> {
  const endDate = await fetchBetaEndDate();
  const now = new Date();
  const diff = endDate.getTime() - now.getTime();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return Math.max(0, days);
}

/**
 * Format beta end date for display (async — reads Firestore config).
 */
export async function formatBetaEndDate(): Promise<string> {
  const endDate = await fetchBetaEndDate();
  return endDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

